// MessageArea drag-and-drop logic — extracted from MessageArea.tsx during
// the P5 pass-2 split.
//
// Owns the "is the user dragging files / cards over the chat" overlay state
// and the three React DOM handlers (`onDragOver`, `onDragLeave`, `onDrop`).
// Drops branch on the first matching dataTransfer type:
//   - `application/x-terminal-card` → mutates a typed message via
//     `mapDroppedCard` so the receiving channel renders the card.
//   - `Files` → calls into the MessageInput's exposed file handler via the
//     ref the input writes when it mounts.

import { useCallback, useState, type RefObject } from "react";
import { useZero } from "@rocicorp/zero/react";
import { toast } from "sonner";
import { mutators } from "@/lib/mutators";
import { mapDroppedCard } from "./card-mapper";

type ZeroClient = ReturnType<typeof useZero>;

interface DragDropDeps {
  user: { id: string } | null | undefined;
  channelId: string | null | undefined;
  zero: ZeroClient;
  filesDroppedRef: RefObject<((files: File[]) => void) | null>;
}

interface DragDropApi {
  isDraggingFiles: boolean;
  isDraggingCard: boolean;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
}

export function useMessageAreaDragDrop({
  user,
  channelId,
  zero,
  filesDroppedRef,
}: DragDropDeps): DragDropApi {
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [isDraggingCard, setIsDraggingCard] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only show overlay if dragging files or terminal cards
    if (e.dataTransfer.types.includes("application/x-terminal-card")) {
      setIsDraggingFiles(true);
      setIsDraggingCard(true);
    } else if (e.dataTransfer.types.includes("Files")) {
      setIsDraggingFiles(true);
      setIsDraggingCard(false);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // relatedTarget is null when leaving the window entirely
    // or check if relatedTarget is outside our container
    const relatedTarget = e.relatedTarget as Node | null;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDraggingFiles(false);
      setIsDraggingCard(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingFiles(false);
      setIsDraggingCard(false);

      // Handle terminal card drops — create a typed message with structured
      // payload. Unknown cardType falls back to kind='text' with the raw JSON
      // in content so no drop is silently dropped.
      const cardData = e.dataTransfer.getData("application/x-terminal-card");
      if (cardData && user && channelId) {
        try {
          const parsed = JSON.parse(cardData);
          const cardType = parsed.cardType ?? parsed.type ?? "generic";
          const { kind, content, payload } = mapDroppedCard(cardType, parsed);
          const now = Date.now();
          zero.mutate(
            mutators.messages.insert({
              id: crypto.randomUUID(),
              channel_id: channelId,
              user_id: user.id,
              content,
              kind,
              payload,
              is_edited: false,
              created_at: now,
              updated_at: now,
            }),
          );
        } catch (err) {
          console.error(
            "[message-area] failed to parse dropped card data:",
            err,
          );
          toast.error("Could not process the dropped card");
        }
        return;
      }

      // Handle file drops
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0 && filesDroppedRef.current) {
        filesDroppedRef.current(files);
      }
    },
    [user, channelId, zero, filesDroppedRef],
  );

  return {
    isDraggingFiles,
    isDraggingCard,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
}
